import React, { useEffect, useState, useCallback } from "react";
import { BrowserRouter, Routes, Route, useNavigate, useLocation, Navigate } from "react-router-dom";
import "@/App.css";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { AuthProvider, useAuth } from "./contexts/AuthContext";
import ProtectedRoute from "./components/ProtectedRoute";
import Header from "./components/Header";
import Footer from "./components/Footer";
import HomePage from "./pages/HomePage";
import JobsPage from "./pages/JobsPage";
import AdminPage from "./pages/AdminPage";
import UsersAdminPage from "./pages/UsersAdminPage";
import TrashPage from "./pages/TrashPage";
import LoginPage from "./pages/LoginPage";
import ActivatePage from "./pages/ActivatePage";
import ForgotPasswordPage from "./pages/ForgotPasswordPage";
import SetupPage from "./pages/SetupPage";
import JobModal from "./components/JobModal";
import JobDetail from "./components/JobDetail";
import { jobsApi, techniciansApi, formatApiError } from "./lib/api";

function AppShell() {
  const [jobs, setJobs] = useState([]);
  const [stats, setStats] = useState({ aktiva: 0, Levererad: 0, Avbokad: 0, total: 0 });
  const [technicians, setTechnicians] = useState([]);
  const [selectedJob, setSelectedJob] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editingJob, setEditingJob] = useState(null);
  const [loading, setLoading] = useState(true);
  const [detailView, setDetailView] = useState(false);
  const [detailRefresh, setDetailRefresh] = useState(0);
  const nav = useNavigate();
  const loc = useLocation();
  const { user } = useAuth();

  const refresh = useCallback(async () => {
    try {
      const [list, st, techs] = await Promise.all([
        jobsApi.list(), jobsApi.stats(), techniciansApi.list(),
      ]);
      setJobs(list); setStats(st); setTechnicians(techs);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  }, []);

  const handleSelectJob = (job) => { setSelectedJob(job); setDetailView(true); };
  const handleBackFromDetail = () => { setDetailView(false); setSelectedJob(null); };

  const handleAdd = () => { setEditingJob(null); setModalOpen(true); };
  const handleEdit = (job) => { setEditingJob(job); setModalOpen(true); };

  const handleSave = async (form) => {
    try {
      if (editingJob) {
        const updated = await jobsApi.update(editingJob.id, form);
        toast.success("Ärende uppdaterat");
        setSelectedJob(updated);
      } else {
        await jobsApi.create(form);
        toast.success("Ärende skapat");
      }
      // Bump refresh token so JobDetail re-fetches history + customer jobs immediately
      setDetailRefresh((n) => n + 1);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); throw e; }
  };

  const handleTrash = async (job) => {
    if (!window.confirm(`Flytta ärende för ${job.customerName} till papperskorgen?`)) return;
    try {
      await jobsApi.trash(job.id);
      toast.success("Flyttat till papperskorgen");
      setDetailView(false); setSelectedJob(null);
      await refresh();
    } catch (e) { toast.error(formatApiError(e)); }
  };

  // Reset detail-view and reload data whenever location changes.
  // Watching loc.key (not just loc.pathname) ensures that clicking a nav
  // item while already on the same route (e.g. Ärenden while on /arenden)
  // still closes the open detail. Without this, selectedJob/detailView
  // would survive the navigation and continue to overlay the destination.
  // This effect also handles the initial mount (loc.key is set on first
  // render), so a separate mount-only refresh is not needed.
  useEffect(() => {
    setDetailView(false);
    setSelectedJob(null);
    refresh();
  }, [loc.pathname, loc.key, refresh]);

  const content = (() => {
    if (detailView && selectedJob) {
      return (
        <JobDetail job={selectedJob}
                   refreshToken={detailRefresh}
                   onBack={handleBackFromDetail}
                   onEdit={handleEdit}
                   onDelete={handleTrash} />
      );
    }
    if (loc.pathname === "/arenden") {
      return <JobsPage jobs={jobs} onSelectJob={handleSelectJob} onAdd={handleAdd} />;
    }
    return <HomePage jobs={jobs} stats={stats}
                     onSelectJob={handleSelectJob}
                     onGoToList={() => nav("/arenden")} />;
  })();

  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">
        {loading ? (
          <div className="text-center py-32 text-slate-400">Laddar…</div>
        ) : content}
      </main>
      <Footer />
      <JobModal open={modalOpen} initial={editingJob} technicians={technicians}
                defaultTechnician={user?.linkedTechnicianName || ""}
                onClose={() => setModalOpen(false)} onSave={handleSave} />
    </div>
  );
}

function AdminShell({ children }) {
  return (
    <div className="App min-h-screen flex flex-col">
      <Header />
      <main className="flex-1">{children}</main>
      <Footer />
    </div>
  );
}

function Root() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <Routes>
          <Route path="/login" element={<LoginPage />} />
          <Route path="/aktivera/:token" element={<ActivatePage />} />
          <Route path="/glomt-losenord" element={<ForgotPasswordPage />} />
          <Route path="/setup" element={<SetupPage />} />

          <Route path="/arenden" element={
            <ProtectedRoute><AppShell /></ProtectedRoute>
          } />
          <Route path="/" element={
            <ProtectedRoute><AppShell /></ProtectedRoute>
          } />

          <Route path="/admin" element={
            <ProtectedRoute requireAdmin>
              <AdminShell><AdminPage /></AdminShell>
            </ProtectedRoute>
          } />
          <Route path="/admin/anvandare" element={
            <ProtectedRoute requireAdmin>
              <AdminShell><UsersAdminPage /></AdminShell>
            </ProtectedRoute>
          } />
          <Route path="/admin/papperskorg" element={
            <ProtectedRoute requireAdmin>
              <AdminShell><TrashPage /></AdminShell>
            </ProtectedRoute>
          } />

          <Route path="*" element={<Navigate to="/" replace />} />
        </Routes>
        <Toaster position="top-right" />
      </AuthProvider>
    </BrowserRouter>
  );
}

export default Root;
